import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatMinor, type AdCampaignDetail, type AdDeliveryAction, type AdDeliveryChangeSummary } from '@agency/contracts';
import { Button, Drawer, Notice, useToast } from '@agency/ui';
import { ApiClientError, post } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtWhen } from './common.tsx';

const statusText = (s: string | null) => (s ? s.toLowerCase().replace(/_/g, ' ') : null);

/**
 * What the provider last reported, plus the state of any pause/resume request. A request is "waiting" until the provider acknowledges it:
 * the reported status does not change on click, and a failed request is stated with its reason instead of being hidden.
 */
export function DeliveryStatus({ deliveryStatus, providerCampaignRef, change }: { deliveryStatus: string | null; providerCampaignRef?: string | null; change: AdDeliveryChangeSummary | null }) {
  const verb = change?.action === 'PAUSE' ? 'Pause' : 'Resume';
  return (
    <span className="ads-delivery">
      <span>{statusText(deliveryStatus) ?? <span className="ag-muted">Awaiting provider status</span>}</span>
      {providerCampaignRef ? <span className="ag-mono ag-muted">{providerCampaignRef}</span> : null}
      {change?.state === 'REQUESTED' ? <span className="ads-delivery__note" data-tone="warning" role="status">{verb} requested {fmtWhen(change.createdAt)}. Waiting for the provider to confirm.</span> : null}
      {change?.state === 'FAILED' ? <span className="ads-delivery__note" data-tone="error">{verb} request failed: {change.lastSafeMessage ?? 'the provider did not accept it'}. The status above is unchanged.</span> : null}
    </span>
  );
}

function useDelivery(campaignId: string, action: AdDeliveryAction, onDone?: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation<AdCampaignDetail, ApiClientError, { expectedVersion: number }>({
    mutationFn: (body) => post(`/ads/campaigns/${campaignId}/${action === 'PAUSE' ? 'pause' : 'resume'}`, body),
    onSuccess: (fresh) => {
      qc.setQueryData(['ads-campaign', campaignId], fresh);
      for (const key of ['ads-overview', 'ads-campaigns', 'ads-activity']) void qc.invalidateQueries({ queryKey: [key] });
      toast({ tone: 'success', message: `${action === 'PAUSE' ? 'Pause' : 'Resume'} requested. The provider has not confirmed it yet.` });
      onDone?.();
    },
    onError: (e) => {
      void qc.invalidateQueries({ queryKey: ['ads-overview'] });
      void qc.invalidateQueries({ queryKey: ['ads-campaign', campaignId] });
      toast({ tone: 'error', message: e.fieldErrors[0]?.message ?? e.message });
    },
  });
}

/**
 * Pause and resume for a published campaign. Pausing stops spend and needs no confirmation; resuming spends again, so it asks first.
 * These buttons are a convenience: the API decides, and refuses with a reason if the campaign, provider or role does not allow it.
 */
export function DeliveryActions({ campaign, size = 'md', allow }: { campaign: { id: string; name: string; version: number; deliveryStatus: string | null; dailyBudgetMinor: number; currency: string; deliveryChange: AdDeliveryChangeSummary | null }; size?: 'sm' | 'md'; /** The server's own verdict (from the campaign detail), when known; otherwise the viewer's permissions decide what is offered. */ allow?: { pause: boolean; resume: boolean } }) {
  const { can } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const pause = useDelivery(campaign.id, 'PAUSE');
  const resume = useDelivery(campaign.id, 'RESUME', () => setConfirming(false));
  const waiting = campaign.deliveryChange?.state === 'REQUESTED';
  const paused = campaign.deliveryStatus === 'PAUSED';
  if (waiting) return null;
  return (
    <>
      {!paused && (allow ? allow.pause : can('ads.edit')) ? <Button size={size} loading={pause.isPending} aria-label={`Pause ${campaign.name}`} onClick={() => pause.mutate({ expectedVersion: campaign.version })}>Pause</Button> : null}
      {paused && (allow ? allow.resume : can('ads.publish')) ? <Button size={size} aria-label={`Resume ${campaign.name}`} onClick={() => setConfirming(true)}>Resume…</Button> : null}
      <Drawer
        open={confirming}
        onClose={() => { setConfirming(false); resume.reset(); }}
        title="Resume this campaign?"
        footer={<><Button onClick={() => { setConfirming(false); resume.reset(); }}>Cancel</Button><Button variant="primary" loading={resume.isPending} onClick={() => resume.mutate({ expectedVersion: campaign.version })}>Request resume</Button></>}
      >
        <p>Resuming lets <strong>{campaign.name}</strong> spend again, up to {formatMinor(campaign.dailyBudgetMinor, campaign.currency)} per day. The provider status changes only after the provider confirms; this page keeps showing “paused” until then.</p>
        {resume.error ? <Notice tone="error" title={resume.error.message}>{resume.error.fieldErrors.length ? <ul className="ads-list">{resume.error.fieldErrors.map((f, i) => <li key={i}>{f.message}</li>)}</ul> : null}</Notice> : null}
      </Drawer>
    </>
  );
}
